import { useLoadingView } from '../miiaFw/composables/useLoadingView'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { useInspectionResultQueryStore } from '@/stores/inspectionResultQueryStore'
import { useNetworkStore } from '@/stores/networkStore'
import { useDisasterStore } from '@/stores/disasterStore'
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import { storeToRefs } from 'pinia'
import {
  alertInvalidZipFile,
  alertNotUploadedData,
  confirmDownloadErrorZipFile,
  confirmDownloadZipFile,
  confirmSelectZipFile,
  notifyCompletedUpload,
  notigyCompletedErrorFileDownload
} from './utils/modal'
import {
  getUpdateData,
  readZipFileListData,
  updateInterruptedInspectionSendStates
} from './utils/dataLoader'
import { createZipBlob } from './download/creator'
import { downloadZipFile } from './download/downloader'
import { uploadJsonData } from './upload/uploader'

export const useZipHandler = () => {
  const networkStore = useNetworkStore()
  const disasterStore = useDisasterStore()
  const miiaFwStore = useMiiaFwStore()
  const { isAuthorized } = storeToRefs(miiaFwStore)
  const { setLoadingView } = useLoadingView()
  const inspectionResultQueryStore = useInspectionResultQueryStore()

  /**
   * zipダウンロード
   */
  const downloadZip = async () => {
    // モーダルで確認
    const isDownloadZip = await confirmDownloadZipFile()
    if (!isDownloadZip) return
    // ダウンロードするデータを取得
    const updateData = await getUpdateData()
    if (!updateData) {
      await alertNotUploadedData()
      return
    }

    setLoadingView(true, 'zipファイルを作成中です。')
    try {
      // zipファイルを作成
      const [zipBlob, fileName] = await createZipBlob(updateData).catch(err => {
        console.error(err)
        throw new GlobalError([ERROR_CODES.ZIP_CREATE_ERROR])
      })
      if (zipBlob == null) {
        return
      }
      // zipファイルをダウンロード
      await downloadZipFile(zipBlob, fileName)
        .then(async () => {
          // zipダウンロード済の中断調査のsendStatesをedited → fetchedに更新する
          await updateInterruptedInspectionSendStates('fetched')
        })
        .catch(err => {
          console.error(err)
          throw new GlobalError([ERROR_CODES.ZIP_DOWNLOAD_ERROR])
        })
    } finally {
      setLoadingView(false)
    }
  }

  /**
   * zipアップロード
   */
  const uploadZip = async () => {
    // オフライン状態であれば、後続の処理を実行しないようにする
    if (!networkStore.getIsOnline) {
      return
    }
    // モーダル表示とファイル選択
    const zipFileList = await confirmSelectZipFile()
    if (!zipFileList) return

    setLoadingView(
      true,
      'zipデータのアップロード中です。件数が多い場合、時間がかかる場合があります。このままお待ちください。'
    )
    try {
      // アップロードするデータを読込
      const uploadDataList = await readZipFileListData(zipFileList).catch(async () => {
        await alertInvalidZipFile()
        return
      })
      if (uploadDataList == null) {
        return
      }
      // アップロード
      const errorData = await uploadJsonData(uploadDataList)
      // 調査結果再読み込み
      await inspectionResultQueryStore
        .fetchQueryState(disasterStore.disasterId, disasterStore.disaster?.govCode)
        .finally(() => {
          setLoadingView(false)
        })
      // エラーデータがない場合はダウンロード完了のモーダルを出して終了
      if (errorData.length === 0) {
        notifyCompletedUpload()
        return
      }

      // エラーデータのダウンロード
      const isDownloadErrorZip = await confirmDownloadErrorZipFile()
      if (!isDownloadErrorZip) return

      setLoadingView(true, 'zipファイルを作成中です。')
      // zipファイルを作成
      const [zipBlob, fileName] = await createZipBlob(errorData).catch(err => {
        console.error(err)
        throw new GlobalError([ERROR_CODES.ZIP_CREATE_ERROR])
      })
      if (zipBlob == null) {
        return
      }
      // zipファイルをダウンロード
      await downloadZipFile(zipBlob, fileName)
        .then(async () => {
          await notigyCompletedErrorFileDownload(fileName)
        })
        .catch(err => {
          console.error(err)
          throw new GlobalError([ERROR_CODES.ZIP_DOWNLOAD_ERROR])
        })
    } finally {
      setLoadingView(false)
    }
  }

  /**
   * ダウンロードが必要か判断
   * @returns 判別結果
   */
  const checkShouldZipDownload = async () => {
    // 未認証時はダウンロード不要
    if (!isAuthorized) return false
    // ダウンロードするデータがある場合はtrue返却
    const updateData = await getUpdateData()
    return !!updateData
  }

  return {
    downloadZip,
    uploadZip,
    checkShouldZipDownload
  }
}
